import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { ResultLabel, Text, IconCalendar } from '../../atoms';
import { Team, IMatchRow, Card, DataCenterSkeleton } from '../../molecules';
import { ComposableList, IComposableListAccessibleItem } from '../../organisms';
import { dateFormat } from '../../../utils/times';
import { getResult } from '../../../utils/match-status';

import './card-football-calendar.scss';

export interface IMatch extends IComposableListAccessibleItem, Omit<IMatchRow, 'trackingData'> {}
export interface IDay {
  title: string;
  date: string;
  round: number;
  matches: Array<IMatch>;
}

export interface ICardFootballCalendar extends IComponent {
  ctaLink?: string;
  competitionId: number;
  customCompetitionName: string;
  days: Array<IDay>;
  isLoading?: boolean;
  trackingData?: {};
  cardCtaTrackingData?: {};
}

interface ICardFootballCalendarClasses {
  wrapper: string;
  match: string;
  matchAway: string;
  matchHome: string;
  matchResult: string;
  emptyWrapper: string;
  emptyIcon: string;
  emptyCta: string;
}

const classNames = (): ICardFootballCalendarClasses =>
  makeClassNames({
    wrapper: 'card-football-calendar',
    match: 'card-football-calendar__match-wrapper',
    matchHome: 'card-football-calendar__match-wrapper__home',
    matchAway: 'card-football-calendar__match-wrapper__away',
    matchResult: 'card-football-calendar__match-wrapper__result',
    matchWrapper: 'card-football-calendar__match-wrapper',
    emptyWrapper: 'card-football-calendar__no-items',
    emptyCta: 'card-football-calendar__no-items__cta',
    emptyIcon: 'card-football-calendar__no-items__icon',
  }) as ICardFootballCalendarClasses;

const CardFootballCalendar: React.FC<ICardFootballCalendar> = ({
  /* istanbul ignore next: default value */ days,
  /* istanbul ignore next: default value */ ctaLink,
  /* istanbul ignore next: default value */ isLoading = false,
  /* istanbul ignore next: default value */ customCompetitionName,
  /* istanbul ignore next: default value */ trackingData = {},
  /* istanbul ignore next: default value */ cardCtaTrackingData = {},
}) => {
  const cn = useMemo(() => classNames(), []);
  const isValidDayList = useMemo(() => Boolean(days && days.length), [days]);
  const calculateDayTitle = useCallback(
    (day: IDay): string => `${day.title} - ${dateFormat({ date: day.date, format: 'dddd D MMM YYYY' })}`,
    []
  );
  const getResultForMatch = useCallback(
    ({ date, home, status, away, showLiveResult }: IMatch) => getResult(status, date, home, away, showLiveResult),
    []
  );

  return (
    <aside role="document" title="Data card calendario" {...objectToDataAttributes(trackingData)}>
      {isLoading ? (
        <DataCenterSkeleton rowCount={5} />
      ) : (
        <Card
          size="small"
          cta={isValidDayList ? t('card.view.all') : t('view.all.results')}
          ctaLink={ctaLink}
          title={`${t('calendar.cta')} ${customCompetitionName}`}
          className={cn.wrapper}
          trackingData={cardCtaTrackingData}
          ctaMobile
          ctaMobileWhite
        >
          {isValidDayList ? (
            days.map((day, index) => (
              <ComposableList
                key={`data-card-match-day-${index}`}
                items={day.matches}
                fields={[
                  {
                    prop: 'title',
                    render: (data) => (
                      <div className={cn.match}>
                        <div className={cn.matchHome}>
                          <Team color="light" {...data.home} />
                        </div>
                        <div className={cn.matchResult}>
                          <ResultLabel itemState={data.status}>{getResultForMatch(data)?.text}</ResultLabel>
                        </div>
                        <div className={cn.matchAway}>
                          <Team color="light" {...data.away} />
                        </div>
                      </div>
                    ),
                  },
                ]}
                headers={{
                  title: calculateDayTitle(day),
                }}
                inverted
              />
            ))
          ) : (
            <div className={cn.emptyWrapper}>
              <div className={cn.emptyIcon}>{<IconCalendar size="medium" color="light" />}</div>

              <div className={cn.emptyCta}>
                <Text color="light" fontSize="20" fontWeight="400">
                  {`${t('card.view.unavailable')} `}
                </Text>
                <Text color="light" fontSize="20" fontWeight="500">
                  {`${t('card.view.unavailable.cta')} `}
                </Text>
                {/* <Cta
                  href={ctaLink}
                  trackingData={{ intcmp: cardCtaTrackingData.cta }}
                  fontSize="20"
                  color="light"
                  fontWeight="500"
                >
                  {t('card.view.unavailable.cta')}
                </Cta>*/}
              </div>
            </div>
          )}
        </Card>
      )}
    </aside>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CardFootballCalendar.propTypes = {
    ctaLink: PropTypes.string,
    customCompetitionName: PropTypes.string.isRequired,
    // @ts-ignore
    days: PropTypes.array,
    isLoading: PropTypes.bool,
  };
}

export default CardFootballCalendar;
